import React, { useCallback, useEffect, useState } from 'react'
import { Button, Form, Spinner, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { DocumentWorkflowDefinition, documentWorkflowDefinitionService } from '../../services/document-workflow-definition.service'
import { PermissionAction, PermissionType } from '../../services/permissions.service'
import { toastService } from '../../services/toast.service'
import { ConfirmDialog } from '../common/ConfirmDialog'
import IfPermissions from '../common/IfPermissions'
import PageHeader from '../common/PageHeader'
import { DocumentWorkflowDefinitionEditDialog } from './DocumentWorkflowDefinitionEditDialog'
import { DocumentWorkflowDefinitionVisualizationDialog } from './DocumentWorkflowDefinitionVisualizationDialog'

export const DocumentWorkflowDefinitionList: React.FC = () => {
  const { t } = useTranslation()
  const [data, setData] = useState<DocumentWorkflowDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editingObject, setEditingObject] = useState<DocumentWorkflowDefinition | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [showVizDialog, setShowVizDialog] = useState(false)
  const [vizDefinitionId, setVizDefinitionId] = useState<number | null>(null)
  const [vizDefinitionName, setVizDefinitionName] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [objectToDelete, setObjectToDelete] = useState<DocumentWorkflowDefinition | null>(null)
  const [deleteSubmitting, setDeleteSubmitting] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await documentWorkflowDefinitionService.listFiltered(1, undefined, 'name', false, undefined, undefined, { page_size: 1000 })
      setData(response.results || [])
    } catch {
      toastService.showError(t('error_loading_data'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleCreate = () => {
    setEditingObject(null)
    setIsCreating(true)
    setShowEditDialog(true)
  }

  const handleEdit = (item: DocumentWorkflowDefinition) => {
    setEditingObject(item)
    setIsCreating(false)
    setShowEditDialog(true)
  }

  const handleViewVisualization = (item: DocumentWorkflowDefinition) => {
    setVizDefinitionId(item.id)
    setVizDefinitionName(item.name)
    setShowVizDialog(true)
  }

  const handleDeleteClick = (item: DocumentWorkflowDefinition) => {
    setObjectToDelete(item)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!objectToDelete) return
    setDeleteSubmitting(true)
    try {
      await documentWorkflowDefinitionService.delete(objectToDelete.id)
      toastService.showSuccess(t('successfully_deleted', { name: objectToDelete.name }))
      loadData()
      setShowDeleteConfirm(false)
      setObjectToDelete(null)
    } catch (e: any) {
      const msg = e?.response?.data?.detail || t('error_deleting')
      toastService.showError(msg)
    } finally {
      setDeleteSubmitting(false)
    }
  }

  const handleEditSuccess = () => {
    setShowEditDialog(false)
    loadData()
  }

  const handleToggleEnabled = async (item: DocumentWorkflowDefinition) => {
    try {
      await documentWorkflowDefinitionService.update(item.id, {
        enabled: !item.enabled,
      })
      loadData()
    } catch {
      toastService.showError(t('error_saving'))
    }
  }

  const stepCount = (item: DocumentWorkflowDefinition) => item.steps?.length ?? 0

  return (
    <div className="document-workflow-definition-list">
      <PageHeader title={t('document_workflow.workflow_definitions_title')} info={t('document_workflow.workflow_definitions_for_type_help')}>
        <IfPermissions action={PermissionAction.Add} type={PermissionType.DocumentWorkflowDefinition}>
          <Button variant="primary" size="sm" onClick={handleCreate}>
            <i className="bi bi-plus-circle" /> {t('create')}
          </Button>
        </IfPermissions>
      </PageHeader>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2 text-muted">{t('loading_data')}</p>
        </div>
      ) : (
        <Table responsive hover>
          <thead>
            <tr>
              <th>{t('name')}</th>
              <th className="d-none d-md-table-cell">{t('document_workflow.description')}</th>
              <th>{t('status')}</th>
              <th>{t('document_workflow.steps_count')}</th>
              <th style={{ width: 140 }}></th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-muted text-center py-4">
                  {t('document_workflow.no_workflow_definitions')}
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr key={item.id} className="align-middle">
                  <td>{item.name}</td>
                  <td className="d-none d-md-table-cell text-muted small">{item.description || '—'}</td>
                  <td>
                    <IfPermissions action={PermissionAction.Change} type={PermissionType.DocumentWorkflowDefinition}>
                      <Form.Check
                        type="switch"
                        id={`document-workflow-definition-${item.id}`}
                        checked={item.enabled}
                        onChange={() => handleToggleEnabled(item)}
                        label={<span className="text-muted">{item.enabled ? t('enabled') : t('disabled')}</span>}
                      />
                    </IfPermissions>
                  </td>
                  <td>{stepCount(item)}</td>
                  <td>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => handleViewVisualization(item)}
                      title={t('document_workflow.visualization')}
                    >
                      <i className="bi bi-diagram-3" />
                    </Button>
                    <IfPermissions action={PermissionAction.Change} type={PermissionType.DocumentWorkflowDefinition}>
                      <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleEdit(item)} title={t('edit')}>
                        <i className="bi bi-pencil" />
                      </Button>
                    </IfPermissions>
                    <IfPermissions action={PermissionAction.Delete} type={PermissionType.DocumentWorkflowDefinition}>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDeleteClick(item)} title={t('delete')}>
                        <i className="bi bi-trash" />
                      </Button>
                    </IfPermissions>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {showEditDialog && (
        <DocumentWorkflowDefinitionEditDialog
          object={editingObject ?? undefined}
          isCreating={isCreating}
          onSuccess={handleEditSuccess}
          onError={(err: unknown) => {
            const message = err instanceof Error && err.message ? err.message : t('error_saving')
            toastService.showError(message, err)
          }}
          onClose={() => setShowEditDialog(false)}
        />
      )}

      {showVizDialog && vizDefinitionId !== null && (
        <DocumentWorkflowDefinitionVisualizationDialog
          definitionId={vizDefinitionId}
          definitionName={vizDefinitionName}
          onClose={() => {
            setShowVizDialog(false)
            setVizDefinitionId(null)
            setVizDefinitionName('')
          }}
        />
      )}

      <ConfirmDialog
        show={showDeleteConfirm}
        title={t('confirm_delete')}
        message={objectToDelete ? t('document_workflow.confirm_delete_definition', { name: objectToDelete.name }) : ''}
        confirmText={deleteSubmitting ? undefined : t('delete')}
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false)
          setObjectToDelete(null)
        }}
      />
    </div>
  )
}
